import {
  Breadcrumb,
  BreadcrumbIcon,
  BreadcrumbLink,
  BreadcrumbPage,
  Button,
  useAlert,
} from '@ads/components-react';
import { Link } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/shared/ui/atoms';
import { PageHeader } from '@/shared/ui/organisms/PageHeader';

import { useTutorClassDocuments } from '../../../hooks/useTutorClassDocuments';
import { useTutorDocumentActions } from '../../../hooks/useTutorDocumentActions';
import { useTutorDocumentUpload } from '../../../hooks/useTutorDocumentUpload';
import { useTutorMemoryConfig } from '../../../hooks/useTutorMemoryConfig';
import { useTutorPersonaConfig } from '../../../hooks/useTutorPersonaConfig';
import { formatCount } from '../../../utils/format';
import { ContextFileList } from '../../organisms/ContextFileList';
import { ContextFileUpload } from '../../organisms/ContextFileUpload';
import { PromptConfigForm } from '../../organisms/PromptConfigForm';

export interface TutorClassDetailTemplateProps {
  readonly classStudentId: number;
  /** Rótulo da turma vindo da listagem. Vazio em acesso direto por URL. */
  readonly classLabel: string;
}

/**
 * Template do detalhe de uma turma no tutor.
 *
 * O rótulo vem por prop e não de um GET: nenhum endpoint do módulo resolve
 * `classStudentId` → nome, e o único que aceita esse id no path devolve configuração de
 * nota e frequência, com dependência de outro controller. Em acesso direto por URL a tela
 * cai no id.
 *
 * Em erro do GET de documentos, o Alert substitui apenas o upload e a lista de documentos —
 * não faria sentido mostrar um empty state sugerindo "turma sem documentos" quando a causa
 * real é a falha de carregamento. O botão de tentar novamente aciona o `refetch` do hook.
 * Persona e memória da turma renderizam independentemente dessa falha: cada
 * `PromptConfigForm` já carrega seu próprio estado de erro e retry, então não há nada a
 * ganhar — e muito a perder — em esconder configuração que carregou com sucesso só porque
 * um endpoint não relacionado falhou.
 *
 * O upload é a única escrita que não pede confirmação prévia: importar e excluir avisam
 * o resultado via toast a partir dos callbacks de `useTutorDocumentActions`, nunca
 * silenciosamente.
 */
export function TutorClassDetailTemplate({
  classStudentId,
  classLabel,
}: TutorClassDetailTemplateProps) {
  const { t } = useTranslation('tutorAI');
  const { alert } = useAlert();

  // Memoizado porque `save` e os callbacks de `usePromptDraft` dependem do escopo: sem isso,
  // um objeto novo a cada render recria esses callbacks sem necessidade. O efeito de
  // ressincronização do rascunho NÃO depende deste objeto — ele usa a chave derivada em
  // string, justamente para não re-sincronizar a cada render.
  const scope = useMemo(() => ({ kind: 'class' as const, classStudentId }), [classStudentId]);

  const persona = useTutorPersonaConfig(scope);
  const memory = useTutorMemoryConfig(scope);

  const { documents, isLoading, isError, refetch } = useTutorClassDocuments(classStudentId);
  // `documents` alimenta a recusa de nome repetido no upload, além da lista.
  const {
    upload,
    isUploading,
    items: uploadItems,
  } = useTutorDocumentUpload(classStudentId, documents);
  const { importDocument, deleteDocument } = useTutorDocumentActions(classStudentId, {
    onImported: ({ chunksGenerated, embedTokensUsed }) =>
      alert({
        alertType: 'success',
        title: t('classDetail.documents.importSuccess', {
          chunks: formatCount(chunksGenerated),
          tokens: formatCount(embedTokensUsed),
        }),
      }),
    onImportError: () =>
      alert({ alertType: 'danger', title: t('classDetail.documents.importError') }),
    onDeleted: () => alert({ alertType: 'success', title: t('classDetail.documents.deleted') }),
    onError: () => alert({ alertType: 'danger', title: t('classDetail.documents.actionError') }),
  });

  const title = classLabel || t('classDetail.fallbackTitle', { id: classStudentId });

  return (
    <div className="flex flex-col gap-xs">
      <PageHeader
        title={title}
        subtitle={t('classDetail.subtitle')}
        breadcrumb={
          <Breadcrumb>
            <BreadcrumbLink>
              <Link to="/">
                <BreadcrumbIcon />
              </Link>
            </BreadcrumbLink>
            <BreadcrumbLink>
              <Link to="/tutor-ai/classes">{t('classes.title')}</Link>
            </BreadcrumbLink>
            <BreadcrumbPage>{title}</BreadcrumbPage>
          </Breadcrumb>
        }
      />

      {isError ? (
        <Alert type="danger">
          <div className="flex w-full flex-wrap items-center justify-between gap-mini">
            <span>{t('classDetail.loadError')}</span>
            <Button hierarchy="secondary" size="sm" onClick={() => void refetch()}>
              {t('classDetail.retry')}
            </Button>
          </div>
        </Alert>
      ) : (
        <>
          <ContextFileUpload onUpload={upload} isUploading={isUploading} items={uploadItems} />

          <ContextFileList
            documents={documents}
            isLoading={isLoading}
            onImport={importDocument}
            onDelete={deleteDocument}
          />
        </>
      )}

      <PromptConfigForm
        title={t('classDetail.config.personaTitle')}
        description={t('classDetail.config.personaDescription')}
        label={t('config.persona.label')}
        placeholder={t('config.persona.placeholder')}
        confirmBody={t('config.confirm.bodyClass')}
        inheritedMessage={t('config.inherited')}
        state={persona}
      />

      <PromptConfigForm
        title={t('classDetail.config.memoryTitle')}
        description={t('classDetail.config.memoryDescription')}
        label={t('config.memory.label')}
        placeholder={t('config.memory.placeholder')}
        confirmBody={t('config.confirm.bodyClass')}
        inheritedMessage={t('config.inherited')}
        state={memory}
      />
    </div>
  );
}
